
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface UseUnreadMessagesOptions {
  userId?: string;
  userType: 'client' | 'practitioner';
  globalRefreshTrigger?: number;
}

interface UnreadMessageCounts {
  totalUnread: number;
  unreadByBooking: Record<string, number>;
}

export function useUnreadMessages({ userId: providedUserId, userType, globalRefreshTrigger }: UseUnreadMessagesOptions) {
  const { user } = useAuth();
  const userId = providedUserId || user?.id;

  const [unreadCounts, setUnreadCounts] = useState<UnreadMessageCounts>({
    totalUnread: 0,
    unreadByBooking: {}
  });
  const [isLoading, setIsLoading] = useState(true);
  const isRefreshing = useRef(false);

  const fetchUnreadCounts = async () => {
    if (!userId || isRefreshing.current) return;
    
    try {
      isRefreshing.current = true;
      console.log('🔍 Fetching unread messages for user:', userId, 'userType:', userType);
      
      const { data, error } = await supabase
        .from('messages')
        .select(`
          id,
          conversation_id,
          is_read,
          sender_type,
          conversations!inner(
            booking_id,
            bookings!inner(
              ${userType === 'client' ? 'client_id' : 'practitioner_id'}
            )
          )
        `)
        .eq(`conversations.bookings.${userType === 'client' ? 'client_id' : 'practitioner_id'}`, userId)
        .neq('sender_type', userType);

      if (error) {
        console.error('Error fetching unread messages:', error);
        return;
      }

      console.log('📨 Raw messages data (filtered by sender_type != userType):', data?.length || 0, 'messages');
      console.log('📨 Messages data sample:', data?.slice(0, 3));

      // Filter to only unread messages (is_read === false or is_read === null)
      const unreadData = data?.filter(msg => msg.is_read === false || msg.is_read === null) || [];
      console.log('📨 Unread messages after filtering:', unreadData.length);

      // Calculate unread counts
      const unreadByBooking: Record<string, number> = {};
      let totalUnread = 0;

      unreadData.forEach(message => {
        const bookingId = message.conversations.booking_id;
        unreadByBooking[bookingId] = (unreadByBooking[bookingId] || 0) + 1;
        totalUnread++;
      });

      console.log('📊 Unread message counts:', { totalUnread, unreadByBooking });
      
      setUnreadCounts({
        totalUnread,
        unreadByBooking
      });
    } catch (error) {
      console.error('Error in fetchUnreadCounts:', error);
    } finally {
      setIsLoading(false);
      isRefreshing.current = false;
    }
  };

  const markMessagesAsRead = async (bookingId: string) => {
    if (!userId) return;
    
    try {
      // First get the conversation_id for this booking
      const { data: conversationData, error: conversationError } = await supabase
        .from('conversations')
        .select('id')
        .eq('booking_id', bookingId)
        .single();

      if (conversationError || !conversationData) {
        console.error('Error finding conversation for booking:', conversationError);
        return;
      }

      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('conversation_id', conversationData.id)
        .eq('is_read', false)
        .neq('sender_type', userType);

      if (error) {
        console.error('Error marking messages as read:', error);
        return;
      }

      // Update local state immediately
      setUnreadCounts(prev => {
        const newUnreadByBooking = { ...prev.unreadByBooking };
        const unreadCount = newUnreadByBooking[bookingId] || 0;
        delete newUnreadByBooking[bookingId];
        
        return {
          totalUnread: prev.totalUnread - unreadCount,
          unreadByBooking: newUnreadByBooking
        };
      });

      console.log('✅ Messages marked as read for booking:', bookingId);
    } catch (error) {
      console.error('Error in markMessagesAsRead:', error);
    }
  };

  // Initial fetch and refresh on globalRefreshTrigger changes
  useEffect(() => {
    if (userId) {
      fetchUnreadCounts();
    }
  }, [userId, userType, globalRefreshTrigger]);

  return {
    unreadCounts,
    isLoading,
    markMessagesAsRead,
    refreshUnreadCounts: fetchUnreadCounts
  };
}
